// Wave 3 NC1/NC3: notification-center data layer. Talks to the dev-native notification feed
// (backend already live: GET/PUT /v1/users/notifications*, merged pre-Wave1 as part of Lane 3)
// via the SAME beta-shaped paths the shim already maps (/users/notifications,
// /users/notifications/unread-count, /users/notifications/{id}/read) -- no new backend route
// needed, this is a dev-native feature with no beta-contract equivalent (same class as Wave 2's
// /users/me/calendar-feed-url). Guarded by isDevBackend() so prod/staging builds without the dev
// shim never call it.
import api from "../api"
import { aliasId } from "@utils/devBackendShim"
import { isDevBackend } from "@utils/devBackendShim"

// Raw dev notification row -> FE shape the panel/reducer consume. Keeps the real notification id
// (a UUID, needed for mark-read) alongside an aliased teeup id (co_id -> the same int id the
// inbox/detail pages already use) so a click can deep-link via /teeUps/{aliasedId}.
const reshapeNotification = r => ({
  id: r.id,
  type: r.type, // "mention" | "nudge" | "reply" | "invite" | "message" | "decide" | "reaction" | "status" | ...
  actorId: r.actor_id,
  actorName: r.actor_name,
  actorAvatar: r.actor_avatar,
  coId: r.co_id,
  teeupId: r.co_id ? aliasId(r.co_id) : null,
  payload: r.payload || {},
  read: !!r.read,
  createdAt: r.created_at,
})

// wave4-notif-conform: unlimited history via lazy-load pagination (legacy spec requirement --
// "There should be a pagination system with lazy load" -- replaces the prior 30-day-window single
// fetch). Backend now returns {items, has_more, offset, limit} instead of a bare array
// (F:\coowe\wt\wave4-notif-conform-backend\api\routers\notifications.py).
const PAGE_SIZE = 30

export const fetchNotifications = async (offset = 0, limit = PAGE_SIZE) => {
  if (!isDevBackend()) return { items: [], hasMore: false }
  try {
    const { data } = await api.client.get("/users/notifications", {
      params: { limit, offset },
    })
    // Tolerate the old bare-array shape too (defensive, in case an older backend image is live).
    const arr = Array.isArray(data) ? data : data?.items || []
    const hasMore = Array.isArray(data) ? false : !!data?.has_more
    return { items: arr.map(reshapeNotification), hasMore }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("[notifications] fetchNotifications failed", error)
    return { items: [], hasMore: false }
  }
}

export const fetchUnreadCount = async () => {
  if (!isDevBackend()) return 0
  try {
    const { data } = await api.client.get("/users/notifications/unread-count")
    return Number(data?.unread || 0)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("[notifications] fetchUnreadCount failed", error)
    return 0
  }
}

// wave4-notif-conform: per-type unread counts -> category rail badges (category rollup itself is
// client-side, see @utils/notificationCategories -- one source of truth for the mapping).
export const fetchUnreadCountByType = async () => {
  if (!isDevBackend()) return {}
  try {
    const { data } = await api.client.get("/users/notifications/unread-count-by-type")
    return data || {}
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("[notifications] fetchUnreadCountByType failed", error)
    return {}
  }
}

export const markNotificationRead = async notificationId => {
  if (!isDevBackend() || !notificationId) return false
  try {
    await api.client.put(`/users/notifications/${notificationId}/read`)
    return true
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("[notifications] markNotificationRead failed", error)
    return false
  }
}

// Wave4-notif item 1: "badge = unread since last panel-open". One network call marks every
// notification read server-side (PUT /users/notifications/read-all, added alongside this) instead
// of the prior N-calls-in-a-Promise.all loop over each unread row (markAllNotificationsReadThunk
// in @actions/notificationActions -- now calls this instead of looping markNotificationRead).
export const markAllNotificationsRead = async () => {
  if (!isDevBackend()) return false
  try {
    await api.client.put("/users/notifications/read-all")
    return true
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("[notifications] markAllNotificationsRead failed", error)
    return false
  }
}
